import React from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import DisplayNoFooter from '../components/DisplayNoFooter';
import DisplaySimpleHeader from '../components/DisplaySimpleHeader';
import ScrollToTopOnMount from '../components/ScrollToTopOnMount';
import { getQueryStringByParameter } from '../helpers/utils';
import { fetchHelper } from '../helpers/fetchHelper';
import OpeningTimesOffice from '../Text/OpeningTimesOffice';
import SupportPhoneNumber from '../Text/SupportPhoneNumber';
import LoadingNotification from '../components/LoadingNotification';

interface EmailVerificationPageProps {
  history: any;
}

interface EmailVerificationPageState {
  verificationCode: string;
  verificationClientId: string;
  codeFromUrl: string | null;
  verificationStatus: 'pending' | 'success' | 'error' | null;
  resendStatus: 'idle' | 'pending' | 'sent' | 'error';
}

class EmailVerificationPage extends React.Component<EmailVerificationPageProps, EmailVerificationPageState> {
  constructor(props: EmailVerificationPageProps) {
    super(props);
    const url = window.location.href;
    const codeFromUrl = getQueryStringByParameter('code', url);
    const clientIdFromUrl = getQueryStringByParameter('clientId', url);
    this.state = {
      verificationCode: '',
      verificationClientId: clientIdFromUrl || '',
      codeFromUrl: codeFromUrl,
      verificationStatus: codeFromUrl && clientIdFromUrl ? 'pending' : null,
      resendStatus: 'idle'
    };
  }

  componentDidMount() {
    document.title = 'Email Verification - SwitchboardFREE';
    if (this.state.codeFromUrl && this.state.verificationClientId) {
      this.verifyEmail(this.state.codeFromUrl, this.state.verificationClientId);
    }
  }

  verifyEmail = async (code: string, clientId: string) => {
    try {
      const response = await fetchHelper.postJson<boolean, { Code: string, ClientId: string }>('/api/Users/VerifyEmail', { Code: code, ClientId: clientId });
      if (response && response.data === true) {
        this.setState({ verificationStatus: 'success' });
      } else {
        this.setState({ verificationStatus: 'error' });
      }
    } catch (error) {
      this.setState({ verificationStatus: 'error' });
    }
  };

  handleVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    this.setState({ verificationStatus: 'pending' });
    this.verifyEmail(this.state.verificationCode, this.state.verificationClientId);
  };

  handleResendEmail = async () => {
    this.setState({ resendStatus: 'pending' });
    try {
      const clientIdInt = parseInt(this.state.verificationClientId, 10);
      const response = await fetchHelper.postJson<boolean, { ClientId: number }>('/api/Users/ResendVerificationEmail', { ClientId: clientIdInt });
      if (response && response.data === true) {
        this.setState({ resendStatus: 'sent' });
      } else {
        this.setState({ resendStatus: 'error' });
      }
    } catch (error) {
      this.setState({ resendStatus: 'error' });
    }
  };

  render() {
    let content;
    if (this.state.verificationStatus === 'pending') {
      content = (
        <div className="pt-30">
          <LoadingNotification isLoading={true} width={100} height={100} />
          <p>Verifying email...</p>
        </div>
      );
    } else if (this.state.verificationStatus === 'success') {
      content = (
        <div className="mt-30">
          <div className="alert alert-success">
            <h6 className='text-500'>Thank you, your email has been verified!</h6>
          </div>
        </div>
      );
    } else if (this.state.verificationStatus === 'error') {
      content = (
        <div className="mt-30">
          <div className="error mt-20 mb-20">
            <span className="icon icon-cross"></span>
          </div>
          <div className="alert alert-danger">
            <h6 className='text-500 mb-30'>Email verification failed. Please try again.</h6>
            {this.state.resendStatus === 'pending' ? (
              <p>Resending email...</p>
            ) : this.state.resendStatus === 'sent' ? (
              <p className="alert alert-success">
                Verification email has been resent. Check your inbox or spam folder.
              </p>
            ) : (
              <p>
                <button className='btn btn-danger mb-30' onClick={this.handleResendEmail}>
                  Resend email
                </button>
              </p>
            )}
            <p className='text-500 mb-30'>
              {this.state.resendStatus === 'sent'
                ? ''
                : 'Click the button above to resend your verification email. '}
              If you still do not receive the email, please allow up to 5 minutes for it to arrive or check your spam folder.
            </p>
            <p>
              Call us on{' '}
              <a className='underline' href={`tel:${SupportPhoneNumber.getPhoneNumber()}`}>
                <SupportPhoneNumber />
              </a>{' '}
              if you need help.
              <br />
              Our offices are open <OpeningTimesOffice />
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="container-fluid">
        <DisplayNoFooter />
        <ScrollToTopOnMount />
        <DisplaySimpleHeader />
        <Helmet>
          <body className="body-simple verification-page bg-light" />
          <meta name="robots" content="noindex, nofollow" />
          <style>
            {`
              #lhnHocInvite, #lhnHelpOutCenter, #lhnHocButton {
                display: none !important;
              }
            `}
          </style>
        </Helmet>
        <div className="row">
          <div className="col-xs-12 text-center pt-50 pb-20">
            <div className='w-600px mx-auto'>
              {content}
              <div className="text-center pt-30 mt-30 bt-1">
                <p className="text-xs">
                  &copy; {new Date().getFullYear()} Liquid11, Sea Lake Road, Lowestoft, Suffolk, NR32 3LQ, Company number:
                  04404380 Regulated by Ofcom.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect()(EmailVerificationPage);
